import * as wizeMobileApi from '@wize-mobile-api';
import { AccountMovementMobile } from '@wize-mobile-api';
import * as model from '@qvs/core';
import {
  Amount,
  AssetAllocation,
  AssetAllocationId,
  CashAccount,
  CashAllocation,
  DepositaryMobileBank,
  LoadingStatus,
  Performance,
  PerformanceMethodEnum,
  PerformancePeriod,
  Portfolio,
  PortfolioInstrument,
  PositionTypeEnum,
  Transaction,
  TransactionOrderTypeEnum
} from '@qvs/core';
import { InstrumentMapper } from '~/modules/instrument/instrument.mapper';
import { DateUtils } from '~/shared/utils/date.utils';
import { groupBy, sortBy } from 'lodash-es';
import { environment } from '#environment';

export class PortfolioMapper {
  public static fromWizePortfolios(portfolios: wizeMobileApi.PortfolioMobile[]): model.Portfolio[] {
    return portfolios.map((portfolio) => PortfolioMapper.fromWizePortfolio(portfolio));
  }

  public static fromWizePortfolio(portfolio: wizeMobileApi.PortfolioMobile): model.Portfolio {
    return {
      name: portfolio.description,
      portfolioNumber: portfolio.id,
      portfolioTypeId: portfolio.portfolioTypeId,
      total: {
        currency: portfolio.valuation?.currencyId,
        value: portfolio.valuation?.totalValuationIncludingInterest
      },
      yTDinOutflow: {
        value: portfolio.valuation?.netYtdInOutFlow || 0,
        currency: portfolio.valuation?.currencyId
      },
      relatedUserIds: portfolio.relatedUserIds,
      portfolioMandateTypeId: portfolio.mandateTypeId,
      performance: {
        currentYearPerformance: undefined,
        previousYearPerformance: undefined,
        inceptionPerformance: undefined
      },
      depositaryBankId: portfolio.depositaryBankId
    };
  }

  public static readonly fromWizeCashAccountTransactions = (transactions: any[]): Transaction[] =>
    transactions?.map(PortfolioMapper.fromWizeCashAccountTransaction);

  public static readonly mapPortfolioInstruments = (portfolio: Portfolio) =>
    portfolio?.assetAllocations?.flatMap((allocation) => allocation?.instruments) ?? [];

  public static readonly fromWizePerformance = (wizePerformance: wizeMobileApi.PerformanceMobile): Performance => {
    if (!wizePerformance) {
      return undefined;
    }
    const { beginDate, endDate, performanceMethod, performanceSubPeriods, ...additionalProps } = wizePerformance;
    return {
      beginDate: DateUtils.new(beginDate).startOf('day').toDate(),
      endDate: DateUtils.new(endDate).startOf('day').toDate(),
      performanceMethod: performanceMethod as PerformanceMethodEnum,
      performanceSubPeriods: PortfolioMapper.fromWizePerformancePeriods(performanceSubPeriods),
      ...additionalProps
    };
  };

  public static readonly fromWizeValuation = (
    portfolioNumber: string,
    allocationMobile: wizeMobileApi.AllocationMobile
  ): Portfolio => {
    const portfolio: Portfolio = {
      total: {
        value: allocationMobile?.totalValuationIncludingInterest
      },
      portfolioNumber,
      assetAllocations: PortfolioMapper.fromWizeValuationLines(allocationMobile)
    };
    portfolio.loadingStatus = allocationMobile ? LoadingStatus.SUCCEEDED : LoadingStatus.FAILED;

    return portfolio;
  };

  public static readonly fromWizeValutationToCashAccount = (
    valuationLineMobile: wizeMobileApi.ValuationLineMobile
  ): CashAccount => ({
    total: {
      value: valuationLineMobile.totalValuationIncludingInterest,
      currency: valuationLineMobile.currencyId
    },
    id: valuationLineMobile.accountId,
    totalInPortfolioCurrency: {
      value: valuationLineMobile.totalValuationExVIncludingInterest,
      currency: valuationLineMobile.currencyExVId
    },
    transactions: [],
    description: valuationLineMobile.description
  });

  public static readonly fromWizeDepositaryBank = (depositaryBank: wizeMobileApi.DepositaryMobileBank): DepositaryMobileBank => ({
    id: depositaryBank.id,
    description: depositaryBank.description,
    identification: depositaryBank.identification,
    shortDescription: depositaryBank.shortDescription
  });

  public static readonly fromWizeDepositaryBanks = (
    depositaryBanks: wizeMobileApi.DepositaryMobileBank[]
  ): DepositaryMobileBank[] => depositaryBanks?.map(PortfolioMapper.fromWizeDepositaryBank);

  private static readonly fromWizePerformancePeriod = (
    wizePerformancePeriod: wizeMobileApi.PerformanceMobile
  ): PerformancePeriod => {
    if (!wizePerformancePeriod) {
      return undefined;
    }
    const { beginDate, endDate, ...additionalProps } = wizePerformancePeriod;
    return {
      beginDate: DateUtils.new(beginDate).startOf('day').toDate(),
      endDate: DateUtils.new(endDate).startOf('day').toDate(),
      ...additionalProps
    };
  };

  private static readonly fromWizeCashAccountTransaction = (transaction: AccountMovementMobile): Transaction =>
    transaction
      ? {
          amount: {
            value: transaction.amount,
            currency: transaction.currencyId
          },
          bookingText: transaction.description,
          cashAccountId: transaction.accountId,
          date: transaction.valueDate ? DateUtils.new(transaction.valueDate).toDate() : null,
          orderType: PortfolioMapper.fromWizeTransactionOrderType(transaction.orderTypeId),
          orderId: transaction.orderId
        }
      : undefined;

  private static readonly fromWizeTransactionOrderType = (orderTypeId: string): TransactionOrderTypeEnum | undefined => {
    switch (orderTypeId) {
      case 'TRIN':
        return TransactionOrderTypeEnum.TRANSFER_IN;
      case 'TROU':
        return TransactionOrderTypeEnum.TRANSFER_OUT;
      case 'IPIN':
        return TransactionOrderTypeEnum.INSTANT_PAYMENT_IN;
      case 'ATOA':
        return TransactionOrderTypeEnum.ACCOUNT_TO_ACCOUNT_TRANSFER;
      case 'CLOS':
        return TransactionOrderTypeEnum.DEBIT_INTEREST;
      case 'CUFE':
        return TransactionOrderTypeEnum.CUSTODY_FEES;
      case 'AFAA':
        return TransactionOrderTypeEnum.ACCOUNT_ADMIN_FEES;
      case 'AC_PURCHASE':
        return TransactionOrderTypeEnum.BUY;
      case 'VE_SELL':
        return TransactionOrderTypeEnum.SELL;
      case 'SC_SUBSCRIPTION':
        return TransactionOrderTypeEnum.SUBSCRIPTION;
      case 'RD_REDEMPTION':
        return TransactionOrderTypeEnum.REDEMPTION;
      default:
        return undefined;
    }
  };

  private static readonly fromWizePerformancePeriods = (
    wizePerformancePeriods: wizeMobileApi.PerformancePeriodMobile[]
  ): PerformancePeriod[] => wizePerformancePeriods?.map(PortfolioMapper.fromWizePerformancePeriod);

  private static readonly fromWizeValuationLines = (allocationMobile: wizeMobileApi.AllocationMobile): AssetAllocation[] =>
    !allocationMobile?.valuationLines?.length
      ? []
      : PortfolioMapper.fromValuationLinesGroupByLevel1ClassificationId(allocationMobile);

  private static readonly fromValuationLinesGroupByLevel1ClassificationId = (
    allocationMobile: wizeMobileApi.AllocationMobile
  ): AssetAllocation[] => {
    let assetAllocations = Object.entries(groupBy(allocationMobile.valuationLines, 'level1ClassificationId')).map(
      (valuationLineMobileLevel1Group: [key: string, value: wizeMobileApi.ValuationLineMobile[]]): AssetAllocation => {
        // this is how Object.entries() works: ...[0] --> key, ...[1] --> value:
        const assetAllocation = allocationMobile.allocationLines.find(
          (allocation) => allocation.level1ClassificationId === valuationLineMobileLevel1Group[0]
        );
        const valuationLineMobilesForThislevel1ClassificationId: wizeMobileApi.ValuationLineMobile[] =
          valuationLineMobileLevel1Group[1];

        // some fields are the same in all valuationLines in the same group (e.g., `level1Description`): for those fields we can
        // take e.g. the first element:
        const firstValuationLineMobileLevel1 = valuationLineMobilesForThislevel1ClassificationId[0];

        return {
          id: assetAllocation.level1ClassificationId as AssetAllocationId,
          description: firstValuationLineMobileLevel1.level1Description,
          ranking: firstValuationLineMobileLevel1.level1Ranking,
          total: {
            value: assetAllocation.totalValuationExVIncludingInterest,
            currency: firstValuationLineMobileLevel1.currencyId
          },
          ...(AssetAllocationId.CASH.includes(assetAllocation.level1ClassificationId)
            ? PortfolioMapper.fromWizeValuationToCashAllocation(valuationLineMobilesForThislevel1ClassificationId)
            : {
                instruments: valuationLineMobilesForThislevel1ClassificationId.map((valuationLineMobile) =>
                  PortfolioMapper.fromWizeValuationToSecurityInstrument(valuationLineMobile)
                )
              })
        };
      }
    );

    if (!environment.featureFlags.dynamicAssets) {
      assetAllocations = PortfolioMapper.ensureAllRequiredAssetAllocationsExistInCorrectOrder(assetAllocations);
    } else {
      assetAllocations = sortBy(assetAllocations, 'level1Ranking');
    }

    return assetAllocations;
  };

  private static readonly ensureAllRequiredAssetAllocationsExistInCorrectOrder = (
    assetAllocations: AssetAllocation[]
  ): AssetAllocation[] => {
    const requiredAssetIds = [
      AssetAllocationId.CASH,
      AssetAllocationId.FIXED_INCOME,
      AssetAllocationId.EQUITIES,
      AssetAllocationId.COMMODITIES,
      AssetAllocationId.ALTERNATIVES,
      AssetAllocationId.HOLDINGS,
      AssetAllocationId.LOANS,
      AssetAllocationId.GUARANTEES
    ];

    const missingAssetIds = requiredAssetIds.filter((id) => !assetAllocations.some((asset) => asset.id === id));

    missingAssetIds.forEach((missingId) => {
      assetAllocations.push({
        id: missingId
      });
    });

    return assetAllocations.sort((a, b) => requiredAssetIds.indexOf(a.id) - requiredAssetIds.indexOf(b.id));
  };

  private static readonly fromWizeValuationToCashAllocation = (
    valuationLineMobiles: wizeMobileApi.ValuationLineMobile[]
  ): CashAllocation => {
    if (!environment.featureFlags.dynamicAssets) {
      const fiduciaryDeposits: Amount = {
        value: 0,
        currency: null
      };
      const forexForwards: Amount = {
        value: 0,
        currency: null
      };

      const accounts = [];
      valuationLineMobiles.forEach((valuationLineMobile) => {
        switch (valuationLineMobile.level2ClassificationId) {
          case 'ACCOUNTS':
            accounts.push(PortfolioMapper.fromWizeValutationToCashAccount(valuationLineMobile));
            break;
          case 'FIDUCIARY':
            fiduciaryDeposits.currency = valuationLineMobile.currencyExVId;
            fiduciaryDeposits.value += valuationLineMobile.totalValuationExVIncludingInterest;
            break;
          case 'FOREX':
            forexForwards.currency = valuationLineMobile.currencyExVId;
            forexForwards.value += valuationLineMobile.totalValuationExVIncludingInterest;
            break;
          default:
            break;
        }
      });

      return {
        accounts,
        fiduciaryDeposits,
        forexForwards
      };
    } else {
      const accounts = [];
      const instruments = [];
      valuationLineMobiles.forEach((valuationLineMobile) => {
        if (valuationLineMobile.positionType === wizeMobileApi.ValuationLineMobile.PositionTypeEnum.ACCOUNT) {
          accounts.push(PortfolioMapper.fromWizeValutationToCashAccount(valuationLineMobile));
        } else {
          instruments.push(PortfolioMapper.fromWizeValuationToSecurityInstrument(valuationLineMobile));
        }
      });

      return {
        accounts,
        instruments
      };
    }
  };

  private static readonly fromWizeValuationToSecurityInstrument = (
    valuationLineMobile: wizeMobileApi.ValuationLineMobile
  ): PortfolioInstrument => ({
    positionType: valuationLineMobile.positionType as PositionTypeEnum,
    amount: {
      value: valuationLineMobile.totalValuationExVIncludingInterest,
      currency: valuationLineMobile.currencyExVId
    },
    positionId: valuationLineMobile?.positionId,
    securityId: valuationLineMobile?.securityId,
    title: valuationLineMobile?.description,
    assetType: InstrumentMapper.fromWizeAssetClassType(valuationLineMobile?.assetClassType),
    price: valuationLineMobile?.price
      ? {
          value: valuationLineMobile?.price,
          currency: valuationLineMobile?.currencyId
        }
      : undefined,
    priceDate: valuationLineMobile?.priceDate,
    isTradingAllowed: valuationLineMobile?.tradingAllowed,
    units: valuationLineMobile.units,
    commitment: valuationLineMobile.commitment,
    averagePurchasePrice: valuationLineMobile.grossPurchasePrice,
    capitalCalled: valuationLineMobile.capitalCalled,
    capitalDistributed: valuationLineMobile.capitalDistributed,
    initialPurchaseDate: valuationLineMobile.inceptionDate ? DateUtils.new(valuationLineMobile.inceptionDate).toDate() : null,
    openCommitment: valuationLineMobile.openCommitment,
    iSIN: valuationLineMobile.isin,
    yieldToMaturity: valuationLineMobile.yieldToMaturity,
    performance: valuationLineMobile.performanceSinceInception,
    accruedInterests: valuationLineMobile.accrued
  });
}
